import { Download } from '@mui/icons-material'
import { Box, Tooltip } from '@mui/material'
import { type FormEvent, useRef } from 'react'
import { StatusButton } from '@/components/StatusButton'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import { useExportSearchMutation } from '@/features/search/api'
import { SEARCH_RESULT_EXPORT_LIMIT } from '@/features/search/constants'
import { useSearch } from '@/features/search/hooks/useSearch'
import { setHasExported } from '@/features/search/slice'
import { enqueueToast, toastForMutationResult } from '@/features/toasts/slice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'export results',
  formId: 'export-search-results-button',
  formLocation: 'main',
  formDestination: 'emc/export',
  formSubmitText: 'Export Results',
}

export const ExportResultsButton = () => {
  const gaFormRef = useRef<GaFormHandle>(null)
  const { selectors, searchResults } =
    useSearch()
  const hasExported = useAppSelector(selectors.selectHasExported)
  const dispatch = useAppDispatch()
  const exportQuery = useAppSelector(selectors.selectSearchQueryForExport)

  const [exportSearch, request] = useExportSearchMutation()

  const {
    details: { totalCount, exceedsMax },
  } = searchResults

  const disabled = !exportQuery || !totalCount || hasExported
  const showExportWarning = exceedsMax

  const { markPending } = useDeferredGaSubmit(gaFormRef, request)

  const handleSubmit = (_e?: FormEvent<HTMLFormElement>) => {
    if (request.isLoading || !exportQuery?.criteriaString) return
    markPending()

    const { criteriaString, ...exportBody } = exportQuery
    exportSearch({ ...exportBody, searchCriteria: criteriaString }).then(
      (result) => {
        dispatch(
          enqueueToast(
            toastForMutationResult(result, {
              successMessage:
                'Exporting of results has started, and will be emailed upon completion.',
              errorMessages: {
                forbidden: "You don't have permission to export results.",
                tooManyRequests:
                  'You have reached your export limit for this hour. Please try again later.',
                default: 'Export request failed. Please try again.',
              },
              dismissMode: 'search-dismisses',
            }),
          ),
        )
        if ('data' in result) dispatch(setHasExported(true))
      },
    )
  }

  const gaFormProps: Omit<GaFormProps, 'children'> = {
    ...baseGaFormProps,
    getFormContent: () =>
      exportQuery?.criteriaString
        ? `Export Criteria: ${exportQuery.criteriaString}`
        : null,
    formStatus: request.isError
      ? 'error'
      : request.isSuccess
        ? 'success'
        : null,
    deferGaSubmit: true,
    onSubmit: handleSubmit,
  }

  return (
    <GaForm ref={gaFormRef} {...gaFormProps}>
      <Tooltip
        title={
          showExportWarning
            ? `Only the first ${SEARCH_RESULT_EXPORT_LIMIT.toLocaleString()} records will be exported.`
            : ''
        }
        arrow
      >
        <Box component="span">
          <StatusButton
            id="export-search-results-button"
            type="submit"
            disabled={disabled}
            isLoading={request.isLoading}
            isSuccess={request.isSuccess}
            isError={request.isError}
            startIcon={<Download />}
          >
            Export Results
          </StatusButton>
        </Box>
      </Tooltip>
    </GaForm>
  )
}
